import type { VoucherView } from '@voll-intelligence/domain';

import type { AppIconName } from '@web/ui/core/atoms';

import { formatDate, formatRawDate } from './format_date';

/** "<origin> → <destination>", or a dash when neither side is present. */
function journeyRoute(voucher: VoucherView): string {
  const route = [voucher.journey?.origin, voucher.journey?.destination].filter(Boolean).join(' → ');
  return route || '—';
}

/**
 * The voucher headline shown on the row and the drawer hero. For modais whose
 * journey "route" is meaningless (a HOTEL's `origin → destination` is the same
 * address twice; a RENTAL's is the agency twice) we show the place name instead.
 * FLIGHT/BUS/MOBILITY and detail-less vouchers keep the origin → destination route.
 */
export function voucherHeadline(voucher: VoucherView): string {
  const details = voucher.details;
  if (details?.kind === 'HOTEL') {
    return [details.name, details.city].filter(Boolean).join(' · ') || journeyRoute(voucher);
  }
  if (details?.kind === 'RENTAL') {
    const agency = details.pickupAgency;
    return [agency?.name, agency?.city].filter(Boolean).join(' · ') || journeyRoute(voucher);
  }
  return journeyRoute(voucher);
}

/**
 * The SERVICE date (the journey/departure day), which is what a consultant
 * scanning a voucher expects to see. Raw-string-preferred so the producer's
 * wall-clock survives. Modais with no journey fall back to the request date.
 */
export function voucherServiceDate(voucher: VoucherView): string {
  if (voucher.journey?.startAt != null) {
    return formatRawDate(voucher.journey.startLocal, voucher.journey.startAt);
  }
  return formatDate(voucher.createdAt);
}

/**
 * The tonal type icon. An inactive (cancelled/expired/declined) voucher always
 * reads as `x-circle`; otherwise the icon follows the modal.
 */
export function voucherTypeIcon(voucher: VoucherView): AppIconName {
  const status = voucher.status;
  if (status === 'CANCELLED' || status === 'DECLINED' || status === 'EXPIRED') {
    return 'x-circle';
  }
  switch (voucher.details?.kind ?? voucher.modal) {
    case 'FLIGHT':
      return 'airplane';
    case 'HOTEL':
      return 'bed';
    case 'RENTAL':
    case 'MOBILITY':
      return 'car';
    case 'PAYMENT':
      return 'credit-card';
    case null: // neither details nor a modal
    default:
      return 'receipt';
  }
}
